import React, { useState } from 'react';
import { Button } from '../ui/Button/Button';
import type { MergedGrades } from '../../types/merge';
import type { Criterion } from '../../types/evaluation';

interface GradesMergeProps {
  projectIds: [string, string];
  mergedAspects: Criterion[];
  onComplete: (data: MergedGrades) => void;
  onBack: () => void;
  onFinalize: () => void;
  initialData?: MergedGrades;
}

export const GradesMerge: React.FC<GradesMergeProps> = ({
  projectIds,
  mergedAspects,
  onComplete,
  onBack,
  onFinalize,
  initialData
}) => {
  const [gradeData, setGradeData] = useState(initialData?.merged || []);
  const [userEdited, setUserEdited] = useState(initialData?.userEdited || false);

  const handleComplete = () => {
    const finalData: MergedGrades = {
      originalProjects: {},
      merged: gradeData,
      boundaries: [],
      userEdited
    };
    
    onComplete(finalData);
    // 最終ステップなので直接確定処理を呼び出す
    onFinalize();
  };

  return (
    <div className="grades-merge">
      <div className="merge-header">
        <h2>等級統合（最終ステップ）</h2>
        <p>最終的な等級システムを統合して新しいプロジェクトを作成します</p>
        <div className="instructions">
          <h4>💡 操作方法</h4>
          <ul>
            <li>両プロジェクトの等級システムを分析・統合します</li>
            <li>等級境界の調整を行います</li>
            <li>統合完了後、新しいプロジェクトとして保存されます</li>
            <li>元のプロジェクトは変更されません</li>
          </ul>
        </div>
      </div>

      <div className="completion-summary">
        <div className="summary-card">
          <h3>🎉 統合準備完了</h3>
          <p>以下のデータが統合されます：</p>
          <div className="completion-stats">
            <div className="stat-item">
              <span className="stat-icon">🎯</span>
              <span className="stat-text">評価対象が統合されました</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">📋</span>
              <span className="stat-text">観点が統合され、編集されました</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">📊</span>
              <span className="stat-text">ルーブリックが調整されました</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">⭐</span>
              <span className="stat-text">評価と等級が統合されました</span>
            </div>
          </div>
        </div>
      </div>

      <div className="merge-summary">
        <div className="summary-sections">
          <div className="summary-section">
            <h4>📊 統合されたデータ</h4>
            <div className="data-overview">
              <div className="overview-item">
                <span className="label">統合対象プロジェクト:</span>
                <span className="value">2件</span>
              </div>
              <div className="overview-item">
                <span className="label">統合された観点:</span>
                <span className="value">{mergedAspects.length}件</span>
              </div>
              <div className="overview-item">
                <span className="label">ルーブリック:</span>
                <span className="value">調整済み</span>
              </div>
              <div className="overview-item">
                <span className="label">評価位置:</span>
                <span className="value">統合済み</span>
              </div>
            </div>
          </div>

          <div className="summary-section">
            <h4>🎯 統合結果</h4>
            <div className="result-info">
              <p>✅ すべてのマージステップが完了しました</p>
              <p>✅ データの整合性が確認されました</p>
              <p>✅ 新しいプロジェクトの作成準備が整いました</p>
            </div>
          </div>
        </div>
      </div>

      <div className="final-actions">
        <div className="action-warning">
          <h4>⚠️ 最終確認</h4>
          <p>「統合を完了」をクリックすると、新しいプロジェクトが作成されます。</p>
          <p>この操作は取り消せません。</p>
        </div>
        
        <div className="merge-actions">
          <Button variant="ghost" onClick={onBack}>
            戻る
          </Button>
          <Button variant="primary" onClick={handleComplete}>
            統合を完了
          </Button>
        </div>
      </div>

      <style>{`
        .grades-merge {
          padding: 1rem;
        }

        .merge-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .merge-header h2 {
          font-size: 1.5rem;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .merge-header p {
          color: #6b7280;
          margin: 0 0 1rem 0;
        }

        .instructions {
          background: #f0f9ff;
          border: 1px solid #bae6fd;
          border-radius: 0.5rem;
          padding: 1rem;
          margin-top: 1rem;
        }

        .instructions h4 {
          color: #0c4a6e;
          margin: 0 0 0.5rem 0;
          font-size: 1rem;
        }

        .instructions ul {
          color: #075985;
          margin: 0;
          padding-left: 1.5rem;
        }

        .instructions li {
          margin-bottom: 0.25rem;
        }

        .completion-summary {
          display: flex;
          justify-content: center;
          margin-bottom: 2rem;
        }

        .summary-card {
          background: #f0fdf4;
          border: 1px solid #10b981;
          border-radius: 0.75rem;
          padding: 2rem;
          max-width: 500px;
          text-align: center;
        }

        .summary-card h3 {
          color: #065f46;
          margin: 0 0 1rem 0;
        }

        .summary-card p {
          color: #047857;
          margin: 0 0 1.5rem 0;
        }

        .completion-stats {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          text-align: left;
        }

        .stat-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .stat-icon {
          font-size: 1.25rem;
        }

        .stat-text {
          color: #065f46;
          font-weight: 500;
        }

        .merge-summary {
          margin-bottom: 2rem;
        }

        .summary-sections {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
        }

        .summary-section {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          padding: 1.5rem;
        }

        .summary-section h4 {
          color: #1f2937;
          margin: 0 0 1rem 0;
          font-size: 1.125rem;
        }

        .data-overview {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .overview-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.5rem 0;
          border-bottom: 1px solid #f3f4f6;
        }

        .overview-item:last-child {
          border-bottom: none;
        }

        .overview-item .label {
          color: #6b7280;
          font-size: 0.875rem;
        }

        .overview-item .value {
          color: #1f2937;
          font-weight: 600;
        }

        .result-info {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .result-info p {
          margin: 0;
          color: #047857;
          font-size: 0.875rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .final-actions {
          background: #fffbeb;
          border: 1px solid #fbbf24;
          border-radius: 0.75rem;
          padding: 1.5rem;
        }

        .action-warning {
          text-align: center;
          margin-bottom: 1.5rem;
        }

        .action-warning h4 {
          color: #92400e;
          margin: 0 0 0.5rem 0;
          font-size: 1.125rem;
        }

        .action-warning p {
          color: #b45309;
          margin: 0 0 0.25rem 0;
          font-size: 0.875rem;
        }

        .merge-actions {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
        }

        @media (max-width: 768px) {
          .merge-actions {
            flex-direction: column-reverse;
          }

          .completion-stats {
            align-items: center;
          }

          .stat-item {
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};